import { isAxiosError, isCancel } from 'axios'
import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { ConversionsAnalyticsApi } from 'entities/Analytics'
import { type IAnalyticsParams, type IConversionListParams } from 'entities/Analytics/api/types'
import { RowConversion } from 'entities/Analytics/model/Table/rowConversion'
import { Conversion } from 'entities/Conversion'
import { tableLimitStore } from 'entities/TableLimit'

export class ConversionsTableStore {
  private _tableStore: TableStore<RowConversion>
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null

  private _error: string | null = null
  private _conversions: Conversion[] = []
  private _hasRetriedAtMinLimit = false
  private _isRetrying = false

  constructor(private _analyticsFiltersParams: () => IAnalyticsParams) {
    this._tableStore = new TableStore<RowConversion>({
      key: 'conversionsTable',
      limit: 10,
      getLimit: () => tableLimitStore.getLimit('conversions-table'),
      setLimit: (value) => tableLimitStore.setLimit('conversions-table', value),
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.loadConversions()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => {
        this._requestLoadConversions.debounceLoadData()
      }
    )
  }

  get tableStore() {
    return this._tableStore
  }

  get error() {
    return this._error
  }

  get conversionsRows() {
    return this._conversions.map((conversion) => new RowConversion(conversion))
  }

  get paramsWithoutPage(): Omit<IConversionListParams, 'page'> {
    return {
      ...this._analyticsFiltersParams(),
      sortBy: this._tableStore.sortBy,
      sortOrder: this._tableStore.sortOrder,
      length: this._tableStore.limit,
    }
  }

  get paramsGetItems(): IConversionListParams {
    return {
      ...this.paramsWithoutPage,
      page: this._tableStore.page,
    }
  }

  private _requestLoadConversions = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      this._tableStore.setLoading(true)

      runInAction(() => {
        this._error = null
      })

      try {
        const {
          data: { data, meta },
        } = await ConversionsAnalyticsApi.getConversions(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })

        runInAction(() => {
          this._conversions = data.map((conversion) => new Conversion(conversion))
          this._hasRetriedAtMinLimit = false
        })
        this._tableStore.setTotal(meta.total)
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: conversions-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this._tableStore.limit
          const newLimit = tableLimitStore.reduceLimit('conversions-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
              this._isRetrying = true
            })
            this._tableStore.setLimit(newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
              this._isRetrying = true
            })
            this._requestLoadConversions.debounceLoadData()
          } else {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
            })
            throw error
          }
        } else {
          throw error
        }
      }
    },
    errorCallback: (error) => {
      if (!isCancel(error)) {
        this._error = 'Failed to load conversions data'
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        if (!this._isRetrying) {
          this._tableStore.setLoading(false)
        }
        this._isRetrying = false
      })
    },
  })

  loadConversions = async () => {
    return this._requestLoadConversions.loadData()
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadConversions.dispose()
    this._tableStore.dispose()
  }
}
